import { Fragment } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Disclosure, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { Dropdown, DropdownItem } from "../common/Dropdown";
import { toQueryString } from "./RoundsFilter";
import { getRoundSelectionParamsFromUrlParams, RoundStatus } from "./hooks/useFilterRounds";
import { getFilterLabel } from "./utils/getFilterLabel";
import { getEnabledChains } from "../../app/chainConfig";
import { ROUND_PAYOUT_DIRECT, ROUND_PAYOUT_MERKLE } from "common";
import { getConfig } from "common/src/config";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const isAlloV2 = getConfig().allo.version === "allo-v2";
export const FILTER_OPTIONS = [{
  label: "All",
  value: "",
  children: []
}, {
  label: "Type",
  value: "type",
  children: [{
    label: "Quadratic funding",
    value: isAlloV2 ? "allov2.DonationVotingMerkleDistributionDirectTransferStrategy" : ROUND_PAYOUT_MERKLE
  }, {
    label: "Direct grants",
    value: isAlloV2 ? "allov2.DirectGrantsSimpleStrategy" : ROUND_PAYOUT_DIRECT
  }]
}, {
  label: "Status",
  value: "status",
  children: [{
    label: "Verified",
    value: "verified"
  }, {
    label: "Active",
    value: RoundStatus.active
  }, {
    label: "Taking applications",
    value: RoundStatus.taking_applications
  }, {
    label: "Finished",
    value: RoundStatus.finished
  }]
}, {
  label: "Network",
  value: "network",
  children: getEnabledChains().map(_ref => {
    let {
      id,
      prettyName
    } = _ref;
    return {
      label: `Rounds on ${prettyName}`,
      value: String(id)
    };
  })
}];
export function FilterDropdown() {
  const [params] = useSearchParams();
  const filter = getRoundSelectionParamsFromUrlParams(params);
  const {
    status = "",
    type = "",
    network = ""
  } = filter;
  const selected = getFilterLabel({
    status,
    type,
    network
  });
  return /*#__PURE__*/_jsx(Dropdown, {
    label: selected === null || selected === void 0 ? void 0 : selected.label,
    options: FILTER_OPTIONS,
    keepOpen: true,
    renderItem: _ref2 => {
      var _status$type$network$, _status$type$network$2;
      let {
        label,
        value: filterKey,
        children,
        close
      } = _ref2;
      // Filters can be multi selected (ie many networks)
      const selectedFilter = (_status$type$network$ = (_status$type$network$2 = {
        status,
        type,
        network
      }[filterKey]) === null || _status$type$network$2 === void 0 ? void 0 : _status$type$network$2.split(",").filter(Boolean)) !== null && _status$type$network$ !== void 0 ? _status$type$network$ : [];
      if (!(children !== null && children !== void 0 && children.length)) {
        return /*#__PURE__*/_jsx(DropdownItem, {
          $as: Link,
          to: `/rounds?${toQueryString({
            ...Object.fromEntries(params),
            network: "",
            status: "",
            type: ""
          })}`,
          preventScrollReset: true,
          onClick: () => close(),
          children: label
        });
      }
      return /*#__PURE__*/_jsx(Disclosure, {
        defaultOpen: Boolean(selectedFilter.length),
        children: /*#__PURE__*/_jsxs("div", {
          className: "relative mt-1",
          children: [/*#__PURE__*/_jsx(Disclosure.Button, {
            className: "relative w-[340px] py-2 pl-3 pr-10 text-left hover:bg-grey-100",
            children: _ref3 => {
              let {
                open
              } = _ref3;
              const Icon = open ? ChevronUpIcon : ChevronDownIcon;
              return /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx("span", {
                  className: "block truncate",
                  children: label
                }), /*#__PURE__*/_jsx("span", {
                  className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2",
                  children: /*#__PURE__*/_jsx(Icon, {
                    className: "h-5 w-5 text-gray-400",
                    "aria-hidden": "true"
                  })
                })]
              });
            }
          }), /*#__PURE__*/_jsx(Transition, {
            as: Fragment,
            leave: "transition ease-in duration-100",
            leaveFrom: "opacity-100",
            leaveTo: "opacity-0",
            children: /*#__PURE__*/_jsx(Disclosure.Panel, {
              static: true,
              className: "mt-1 w-full overflow-auto p-2",
              children: children === null || children === void 0 ? void 0 : children.filter(child => !child.hide).map((child, j) => {
                const isChecked = selectedFilter === null || selectedFilter === void 0 ? void 0 : selectedFilter.includes(child.value);
                const nextValue = isChecked ? // Remove or add the value
                selectedFilter === null || selectedFilter === void 0 ? void 0 : selectedFilter.filter(f => f !== child.value) : selectedFilter === null || selectedFilter === void 0 ? void 0 : selectedFilter.concat(child.value);
                return /*#__PURE__*/_jsx(DropdownItem, {
                  $as: Link,
                  to: `/rounds?${toQueryString({
                    // Merge existing search params (so it doesn't reset sorting or the other selections)
                    ...filter,
                    [filterKey]: nextValue.join(",")
                  })}`,
                  children: /*#__PURE__*/_jsxs("div", {
                    className: "flex gap-2",
                    children: [/*#__PURE__*/_jsx("input", {
                      type: "checkbox",
                      checked: isChecked
                    }), /*#__PURE__*/_jsx("span", {
                      className: `block truncate ${selected ? "font-medium" : "font-normal"}`,
                      children: child.label
                    })]
                  })
                }, j);
              })
            })
          })]
        })
      });
    }
  });
}